import type { AccountView, Edition, LoginCredentials, RegisterCredentials, FirstLoginCredentials, SendResetLinkCredentials, ResetPasswordCredentials, UserProfile, AuthResponse, User } from '@/types/auth'
import { BaseService } from './baseService'
import { API_CONFIG } from '@/config/api'
import { getApiBaseUrl } from '@/config/environment'

class AuthService extends BaseService {
  constructor() {
    super(getApiBaseUrl())
  }

  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    console.log('authService.login called with:', credentials)
    console.log('Making request to /accounts/login/')
    console.log('Credentials JSON:', JSON.stringify(credentials))
    
    const response = await this.requestWithoutAuth<{ access: string; refresh: string; user?: User }>(API_CONFIG.ENDPOINTS.AUTH.LOGIN, {
      method: API_CONFIG.METHODS.POST,
      body: JSON.stringify(credentials),
    })
    
    if (!response.refresh) {
      console.error('Login response missing refresh token; backend should return both access and refresh.', response)
    }
    this.setTokens(response.access, response.refresh ?? '')
    const user = response.user ?? await this.getCurrentUser(response.access)
    console.log('Got user data:', user)
    
    return {
      access: response.access,
      refresh: response.refresh,
      user: user
    }
  }

  async register(credentials: RegisterCredentials): Promise<AuthResponse> {
    console.log('authService.register called with:', credentials)
    console.log('Making request to /accounts/register/')
    console.log('Credentials JSON:', JSON.stringify(credentials))
    
    // Make registration request without authentication token
    const response = await this.requestWithoutAuth<any>(API_CONFIG.ENDPOINTS.AUTH.REGISTER, {
      method: API_CONFIG.METHODS.POST,
      body: JSON.stringify(credentials),
    })
    
    console.log('Register request successful, got response:', response)
    console.log('Response type:', typeof response)
    console.log('Response keys:', Object.keys(response || {}))
    
    // Check if the response contains tokens
    if (response.access && response.refresh) {
      console.log('Found tokens in response, storing them...')
      this.setTokens(response.access, response.refresh)
      
      // Get user data after storing tokens
      console.log('Getting current user data after registration...')
      const user = await this.getCurrentUser()
      console.log('Got user data after registration:', user)
      
      return {
        access: response.access,
        refresh: response.refresh,
        user: user
      }
    } else {
      console.log('No tokens found in response, using user data from registration response...')
      // Use the user data directly from the registration response
      const user = {
        id: response.id || 0,
        email: response.email || credentials.email,
        first_name: response.first_name || credentials.first_name,
        last_name: response.last_name || credentials.last_name,
      }
      console.log('Using user data from registration response:', user)
      return {
        access: '',
        refresh: '',
        user: user
      }
    }
  }

  async firstLogin(credentials: FirstLoginCredentials): Promise<AuthResponse> {
    console.log('authService.firstLogin called with:', credentials)
    console.log('Making request to /accounts/first-login/')
    console.log('Credentials JSON:', JSON.stringify(credentials))
    
    // Make first login request without authentication token
    const response = await this.requestWithoutAuth<any>(API_CONFIG.ENDPOINTS.AUTH.FIRST_LOGIN, {
      method: API_CONFIG.METHODS.POST,
      body: JSON.stringify(credentials),
    })
    
    console.log('First login request successful, got response:', response)
    
    // Store tokens and return user data
    if (response.access && response.refresh) {
      console.log('Found tokens in response, storing them...')
      this.setTokens(response.access, response.refresh)
      
      return {
        access: response.access,
        refresh: response.refresh,
        user: response.user
      }
    } else {
      throw new Error('Invalid response from first login endpoint')
    }
  }

  async getCurrentUser(accessToken?: string): Promise<User> {
    if (accessToken) {
      return this.requestWithToken<User>(API_CONFIG.ENDPOINTS.AUTH.ME, accessToken)
    }
    return this.request<User>(API_CONFIG.ENDPOINTS.AUTH.ME)
  }

  /** Persist the last used app view (researcher/respondent) on the account. */
  async updateActiveView(view: AccountView): Promise<User> {
    return this.request<User>(API_CONFIG.ENDPOINTS.AUTH.ME, {
      method: API_CONFIG.METHODS.PATCH,
      body: JSON.stringify({ active_view: view }),
    })
  }

  /**
   * Move the signed-in account between the two editions. Admins only — the
   * backend refuses anybody without `can_view_admin_panel`.
   *
   * This is a preview switch, not an upgrade: it changes which screens open and
   * nothing else. The respondent allowance stays as it was, and studies keep
   * the edition they were created in, so flipping to Pro does not reshape work
   * already collected.
   */
  async switchEdition(edition: Edition): Promise<User> {
    return this.request<User>(API_CONFIG.ENDPOINTS.AUTH.ME_EDITION, {
      method: API_CONFIG.METHODS.POST,
      body: JSON.stringify({ edition }),
    })
  }

  async refreshTokenWithToken(refreshToken: string): Promise<{ access: string; refresh: string }> {
    return this.request<{ access: string; refresh: string }>(API_CONFIG.ENDPOINTS.TOKEN.REFRESH, {
      method: API_CONFIG.METHODS.POST,
      body: JSON.stringify({ refresh: refreshToken }),
    })
  }

  // Override the base class method to match the signature
  async refreshTokenInternal(): Promise<{ access: string; refresh: string }> {
    const refreshToken = this.getRefreshToken()
    if (!refreshToken) {
      throw new Error('No refresh token available')
    }
    return this.refreshTokenWithToken(refreshToken)
  }

  async logout(): Promise<void> {
    const refreshToken = localStorage.getItem('refresh_token')
    if (refreshToken) {
      try {
        await this.request(API_CONFIG.ENDPOINTS.AUTH.LOGOUT, {
          method: API_CONFIG.METHODS.POST,
          body: JSON.stringify({ refresh: refreshToken }),
        })
      } catch (error) {
        // Ignore logout errors, just clear local storage
        console.warn('Logout request failed:', error)
      }
    }
  }

  setTokens(accessToken: string, refreshToken: string): void {
    if (accessToken != null && accessToken !== '') {
      localStorage.setItem('access_token', accessToken)
    }
    if (refreshToken != null && refreshToken !== '') {
      localStorage.setItem('refresh_token', refreshToken)
    }
  }

  getAccessToken(): string | null {
    return localStorage.getItem('access_token')
  }

  getRefreshToken(): string | null {
    return localStorage.getItem('refresh_token')
  }

  clearTokens(): void {
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
  }

  isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      const currentTime = Date.now() / 1000
      return payload.exp < currentTime
    } catch {
      return true
    }
  }

  async sendResetLink(credentials: SendResetLinkCredentials): Promise<void> {
    console.log('authService.sendResetLink called with:', credentials)
    console.log('Making request to /accounts/send-reset-password-link/')
    
    await this.requestWithoutAuth<void>(API_CONFIG.ENDPOINTS.AUTH.SEND_RESET_LINK, {
      method: API_CONFIG.METHODS.POST,
      body: JSON.stringify(credentials),
    })
    
    console.log('Reset link request successful')
  }

  async resetPassword(credentials: ResetPasswordCredentials): Promise<void> {
    console.log('authService.resetPassword called with:', credentials)
    console.log('Making request to /accounts/reset-password/')
    
    await this.requestWithoutAuth<void>(API_CONFIG.ENDPOINTS.AUTH.RESET_PASSWORD, {
      method: API_CONFIG.METHODS.POST,
      body: JSON.stringify(credentials),
    })
    
    console.log('Password reset successful')
  }

  // Profile management methods
  async getProfile(): Promise<UserProfile> {
    const response = await this.request<UserProfile>(API_CONFIG.ENDPOINTS.AUTH.PROFILE)
    return response
  }

  async createProfile(profileData: Partial<UserProfile>): Promise<UserProfile> {
    console.log('authService.createProfile called with:', profileData)
    console.log('Making request to /accounts/profile/')
    
    const response = await this.request<UserProfile>(API_CONFIG.ENDPOINTS.AUTH.PROFILE, {
      method: API_CONFIG.METHODS.POST,
      body: JSON.stringify(profileData),
    })
    
    console.log('Profile created successfully:', response)
    return response
  }

  async updateProfile(profileData: Partial<UserProfile>): Promise<UserProfile> {
    console.log('authService.updateProfile called with:', profileData)
    console.log('Making request to /accounts/profile/')
    
    const response = await this.request<UserProfile>(API_CONFIG.ENDPOINTS.AUTH.PROFILE, {
      method: API_CONFIG.METHODS.PUT,
      body: JSON.stringify(profileData),
    })
    
    console.log('Profile updated successfully:', response)
    return response
  }

  async patchProfile(profileData: Partial<UserProfile>): Promise<UserProfile> {
    console.log('authService.patchProfile called with:', profileData)
    console.log('Making request to /accounts/profile/')
    
    const response = await this.request<UserProfile>(API_CONFIG.ENDPOINTS.AUTH.PROFILE, {
      method: API_CONFIG.METHODS.PATCH,
      body: JSON.stringify(profileData),
    })
    
    console.log('Profile patched successfully:', response)
    return response
  }
}

export const authService = new AuthService()
